import React, { createContext, useContext, useState, useEffect, useMemo } from "react";

import { useMantineTheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";

// ============================================
// Types
// ============================================

export interface AppEnvironmentState {
  /** Viewport is below `sm` breakpoint (mobile phones) */
  isMobile: boolean;
  /** Viewport is between `sm` and `md` breakpoints (tablets) */
  isTablet: boolean;
  /** Viewport is at or above `md` breakpoint */
  isDesktop: boolean;
  /** Viewport is at or above `lg` breakpoint (wide screens) */
  isWideDesktop: boolean;
  /** Browser reports the device is online */
  isOnline: boolean;
  /** Device supports touch input */
  isTouchDevice: boolean;
  /** App is running as installed PWA (standalone mode) */
  isStandalone: boolean;
  /** User prefers reduced motion (accessibility) */
  prefersReducedMotion: boolean;
  /** User prefers dark color scheme at OS level */
  prefersDarkScheme: boolean;
}

export type AppEnvironmentContextValue = AppEnvironmentState;

// ============================================
// Context
// ============================================

const AppEnvironmentContext = createContext<AppEnvironmentContextValue | null>(null);

// ============================================
// Provider
// ============================================

interface AppEnvironmentProviderProps {
  children: React.ReactNode;
}

export const AppEnvironmentProvider: React.FC<AppEnvironmentProviderProps> = ({ children }) => {
  const theme = useMantineTheme();

  // ----- Responsive breakpoints -----
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`) ?? false;
  const isTabletQuery = useMediaQuery(
    `(min-width: ${theme.breakpoints.sm}) and (max-width: ${theme.breakpoints.md})`
  ) ?? false;
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.md})`) ?? false;
  const isWideDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.lg})`) ?? false;

  // ----- Connectivity -----
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // ----- Touch detection -----
  const isTouchDevice = useMemo(() => {
    if (typeof window === "undefined") return false;
    return "ontouchstart" in window || navigator.maxTouchPoints > 0;
  }, []);

  // ----- PWA standalone mode -----
  const isStandalone = useMemo(() => {
    if (typeof window === "undefined") return false;
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      (navigator as any).standalone === true
    );
  }, []);

  // ----- Accessibility / preferences -----
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)") ?? false;
  const prefersDarkScheme = useMediaQuery("(prefers-color-scheme: dark)") ?? false;

  // ----- Memoized value -----
  const value = useMemo<AppEnvironmentContextValue>(
    () => ({
      isMobile,
      isTablet: isTabletQuery,
      isDesktop,
      isWideDesktop,
      isOnline,
      isTouchDevice,
      isStandalone,
      prefersReducedMotion,
      prefersDarkScheme,
    }),
    [
      isMobile,
      isTabletQuery,
      isDesktop,
      isWideDesktop,
      isOnline,
      isTouchDevice,
      isStandalone,
      prefersReducedMotion,
      prefersDarkScheme,
    ]
  );

  return (
    <AppEnvironmentContext.Provider value={value}>
      {children}
    </AppEnvironmentContext.Provider>
  );
};

// ============================================
// Hook
// ============================================

export const useAppEnvironment = (): AppEnvironmentContextValue => {
  const context = useContext(AppEnvironmentContext);

  if (!context) {
    throw new Error("useAppEnvironment must be used within an AppEnvironmentProvider");
  }

  return context;
};